import React, { useState, useEffect, useMemo } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import SimReplacement from 'assets/img/numbers/settings/sim-card-replacement.svg';
import CustomSIMInput from 'components/CustomInput/CustomSIMInput';
import NumberInfo from 'views/Numbers/Details/common/NumberInfo';
import { BodyS } from 'components/Typography/Typography';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import moment from 'moment';
import { getProductCharacteristic } from 'views/Numbers/helpers/getters';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { useDispatch } from 'react-redux';
import { ProductsService } from 'services/products/products';
import { setToast } from 'redux/modules/toast/actions';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { CustomerAccount } from 'utils/api/models/accounts/customerAccountResponse';
import { ResourceInfo } from 'utils/api/models/products/customerProducts';
import ChangeNumberWrapper from 'views/Auth/common/ChangeNumberWrapper';
import OTP from 'components/OTP/OTP';
import { UsersService } from 'services/users/users';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { CardType } from './SimDialog';
import OtpTab from '../../Settings/subcomponents/ResetAdslPasswordDialog/subcomponents/OtpTab';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';
import { NO_TRANSLATIONS } from 'redux/modules/translation/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IReplaceSimCardDialogProps {
	isOpen: boolean;
	showDialog: () => void;
	hideDialog: () => void;
	phoneNumber: string;
	accountDetails: string;
	billingType: string;
	customerAccount: CustomerAccount;
	resourceInfo: ResourceInfo | null;
	msisdn: string;
}
const steps = {
	CHANGE: 0,
	OTP: 1,
};

const ReplaceSimCardDialog = ({
	isOpen,
	showDialog,
	hideDialog,
	phoneNumber,
	accountDetails,
	billingType,
	customerAccount,
	resourceInfo,
	msisdn,
}: IReplaceSimCardDialogProps) => {
	const [simNumber, setSimNumber] = useState<string>('');
	const [confirmationDialog, setConfirmationDialog] = useState<boolean>(false);
	const [simNumberValid, setSimNumberValid] = useState<boolean>(false);
	const [showSimCardDialogError, setShowSimCardDialogError] = useState<boolean>(false);
	const [helperText, setHelperText] = useState<string | undefined>();
	const [asyncValidationInProgress, setAsyncValidationInProgress] = useState<boolean>(false);
	const classes = useStyles();
	const dispatch = useDispatch();
	const [otp, setOtp] = useState<string>('');
	const [isIncorrectOtp, setIsInCorrectOtp] = useState(false);
	const [isOTPStep, setOTPStep] = useState(false);

	const [activeTab, setActiveTab] = useState(steps.CHANGE);

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		setIsInCorrectOtp(false);
		setOtp('');
		setConfirmationDialog(false);
		setActiveTab(steps.OTP);
		UsersService.requestForOtp(OperationContextEnum.CORPCHANGECREDITLIMIT, msisdn)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				setOTPStep(true);
				setConfirmationDialog(false);
				setToast({
					error: false,
					label: translate('otp.token.sent'),
				});
			});
	};

	const cancelAction = () => {
		switch (activeTab) {
			case steps.CHANGE:
				return hideDialog();
			case steps.OTP: {
				setOTPStep(false);
				return setActiveTab(activeTab - 1);
			}
			default:
				return hideDialog();
		}
	};

	const nextStepDisabled = useMemo(() => {
		switch (activeTab) {
			case steps.CHANGE:
				return !simNumberValid;
			case steps.OTP:
				return otp === '';
			default:
				return false;
		}
	}, [activeTab, simNumberValid, otp]);

	const replace = () => {
		if (simNumberValid) {
			replaceSimCard();
		}
	};

	const cancelConfirmation = () => {
		setConfirmationDialog(false);
		showDialog();
	};

	const replaceSimCard = () => {
		setConfirmationDialog(false);
		setOtp('');
		setOTPStep(false);
		dispatch(showLoadingIndicator());
		const newActivationDate = moment().format('YYYY-MM-DDTHH:mm:ss');
		const accountExternalId = getProductCharacteristic(customerAccount, 'ACCOUNT_EXTERNAL_ID');
		const accountNo = getProductCharacteristic(customerAccount, 'ACCOUNT_NO');
		const subscriberNumber = getProductCharacteristic(customerAccount, 'SUBSCRIBER_NUMBER');
		const oldActivationDate = moment(resourceInfo?.startDate).format('YYYY-MM-DDTHH:mm:ss') || '';
		const oldSimNumber = resourceInfo?.serialNo || '';
		ProductsService.createProductOrder({
			newSimNumber: simNumber,
			phoneNumber,
			billingType,
			accountNumber: accountNo,
			newActivationDate,
			accountExternalId,
			subscriberNumber,
			oldActivationDate,
			oldSimNumber,
			otp: otp,
		})
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setSimNumber('');
				})
			)
			.subscribe(
				({ response }) => {
					closeDialog();
					if (response.createProductOrderResponse.result.result.resultDescription === 'Success') {
						dispatch(
							setToast({
								error: false,
								label: translate('numbers.setting.sim.number.changed'),
								actionType: translate('changed'),
							})
						);
					} else {
						dispatch(
							setToast({
								error: true,
								label: translate('error.invalid.request'),
								actionType: translate('error'),
							})
						);
					}
				},
				(error) => {
					showDialog();
					const code = error?.response?.code ?? '';

					if (code === ERROR_VALIDATING_TOKEN) {
						setIsInCorrectOtp(true);
						setOTPStep(true);
						dispatch(
							setToast({
								error: true,
								label: translate('active.services.add.billing.account.incorrect.password'),
								actionType: translate('error'),
							})
						);
						setActiveTab(steps.OTP);
						return;
					}

					setActiveTab(steps.CHANGE);

					const errorCode = code ? `.${code}` : '';
					let message = translate(`numbers.settings.replace.sim.card.invalid${errorCode}`);

					if (message === NO_TRANSLATIONS) {
						message = translate('error.occured');
					}

					dispatch(
						setToast({
							error: true,
							label: message,
							actionType: translate('error'),
						})
					);
				}
			);

		return () => dispatch(hideLoadingIndicator());
	};

	const clearDialog = (): void => {
		setShowSimCardDialogError(false);
		setSimNumberValid(false);
		setHelperText(undefined);
		setSimNumber('');
		setActiveTab(steps.CHANGE);
		setOtp('');
		setOTPStep(false);
	};

	const closeDialog = (): void => {
		setIsInCorrectOtp(false);
		hideDialog();
		clearDialog();
	};

	const validateSimNumber = () => {
		setAsyncValidationInProgress(true);
		const data = { resourceType: 'SIM', serialNo: simNumber };
		ProductsService.checkResourceAvailable(data).subscribe(
			({ resourceAvailable }) => {
				setAsyncValidationInProgress(false);
				if (resourceAvailable) {
					setConfirmationDialog(true);
				} else {
					setWrongNumber();
				}
			},
			() => {
				setWrongNumber();
			}
		);
	};

	const setWrongNumber = () => {
		setAsyncValidationInProgress(false);
		setShowSimCardDialogError(true);
		setHelperText(translate('numbers.settings.replace.sim.card.invalid'));
	};

	useEffect(() => {
		if (simNumber.length !== 19) {
			setShowSimCardDialogError(false);
			setHelperText(undefined);
		}
	}, [simNumber]);
	return (
		<>
			<CustomDialog
				isOpen={isOpen}
				hideDialog={closeDialog}
				header={translate('numbers.sim.replacement')}
				dialogBody={
					<ChangeNumberWrapper
						tabs={[
							{
								name: translate('numbers.sim.replacement'),
								title: translate('numbers.sim.replacement'),
								content: (
									<div>
										<ConditionalRender
											show={asyncValidationInProgress}
											onTrue={() => (
												<div className={classes.circularProgressContainer}>
													<CustomCircularProgress
														loading
														text={translate(
															'numbers.settings.replace.sim.card.validation.message'
														)}
													/>
												</div>
											)}
										/>
										<NumberInfo phoneNumber={phoneNumber} accountDetails={accountDetails} />
										<div className={classes.inputWrapper}>
											<CustomSIMInput
												labelText={translate('numbers.settings.new.sim.number')}
												formControlProps={{
													fullWidth: true,
												}}
												showError={showSimCardDialogError}
												onErrorChange={setSimNumberValid}
												helperText={helperText}
												supportAsyncValidation={true}
												inputProps={{
													value: simNumber,
													onChange: (value: string) => setSimNumber(value),
												}}
											/>
										</div>
										<img
											src={SimReplacement}
											alt="Sim card replacement"
											className={classes.simReplacementImage}
										/>
									</div>
								),
							},
							{
								name: translate('change.number.verification'),
								title: translate('change.number.verification'),
								content: (
									<ConditionalRender
										show={isOTPStep}
										onTrue={() => (
											<div className={classes.otpWrapper}>
												<OtpTab
													otp={otp}
													setOtp={setOtp}
													sendOtp={requestForOtp}
													otpIncorrect={isIncorrectOtp}
													inOneInput={false}
													submitOTP={replace}
												/>
											</div>
										)}
										onFalse={() => (
											<div>
												<CustomCircularProgress text={translate('loading')} loading={true} />
											</div>
										)}
									/>
								),
							},
						]}
						activeTab={activeTab}
						setActiveTab={setActiveTab}
						buttons={[
							{
								text: activeTab === steps.CHANGE ? translate('cancel') : translate('back'),
								onClick: cancelAction,
								color: 'tertiary-light' as types.theme.buttonColor,
							},
							{
								text: activeTab === steps.OTP ? translate('replace') : translate('next'),
								onClick: activeTab !== steps.OTP ? validateSimNumber : replace,
								color: 'primary-light' as types.theme.buttonColor,
								disabled: nextStepDisabled,
								id: 'next-button',
							},
						]}
					/>
				}
			/>
			<ConfirmationDialog
				isOpen={confirmationDialog}
				hideDialog={() => setConfirmationDialog(false)}
				header={`${translate('numbers.settings.replace.sim.card')}?`}
				dialogBody={
					<div className={classes.confirmationDialogBody}>
						<BodyS>{translate('numbers.settings.are.you.sure')}</BodyS>
						<BodyS>{simNumber}</BodyS>
					</div>
				}
				buttons={[
					{
						text: translate('cancel'),
						onClick: cancelConfirmation,
						color: 'tertiary-light',
					},
					{
						text: translate('replace.it'),
						onClick: requestForOtp,
						color: 'primary-light',
					},
				]}
			/>
		</>
	);
};

export default ReplaceSimCardDialog;
